import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../../components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "../../components/ui/hover-card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../components/ui/command";
import { toast } from "sonner";
import { fmtDateEST, fmtDateTimeEST } from "../../utils/dateUtils";
import { useAppMessage } from "../../components/AppMessage";
import { UserPlus, Copy, Users, Loader2, CheckCircle, Link as LinkIcon, Shield, Trash2, Search, ArrowUpDown, ArrowUp, ArrowDown, Mail, FileText, UserX, UserCheck, RotateCcw, RefreshCw, X, Key, Phone, History, Check, UserCircle } from "lucide-react";
import RolesManagement from "../../components/RolesManagement";
import UserPermissionsDialog from "../../components/UserPermissionsDialog";
import EditUserDialog from "../../components/users/EditUserDialog";
import TelnyxDialog from "../../components/users/TelnyxDialog";
import UserEmailMergeDialog from "../../components/users/UserEmailMergeDialog";
import LoginHistoryDialog from "../../components/users/LoginHistoryDialog";
import ActivityReportDialog from "../../components/users/ActivityReportDialog";
import HrDataDialog, { HRDataHoverCard } from "../../components/users/HrDataDialog";
import LogReportDialog from "../../components/users/LogReportDialog";
import { useAuth } from "../../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

export default function UsersPage() {
  const msg = useAppMessage();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [intendedEmail, setIntendedEmail] = useState("");
  const [isReusable, setIsReusable] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newInviteUrl, setNewInviteUrl] = useState("");
  
  // Cache reference to avoid hitting database on every page visit
  const cacheRef = useRef({
    users: null,
    teams: null,
    invites: null,
    forms: null,
    timestamp: null
  });
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache
  
  // Search and filter state
  const _uf = (() => { try { return JSON.parse(sessionStorage.getItem('jtcrm_users_filters') || '{}'); } catch { return {}; } })();
  const [searchQuery, setSearchQuery] = useState(_uf.searchQuery || "");
  const [filterRole, setFilterRole] = useState(_uf.filterRole || "all");
  const [filterStatus, setFilterStatus] = useState(_uf.filterStatus || "all");
  const [filterExempt, setFilterExempt] = useState(_uf.filterExempt || "all");
  const [filterTeam, setFilterTeam] = useState(_uf.filterTeam || "all");
  const [filterManager, setFilterManager] = useState(_uf.filterManager || "all");
  const [filterNoTeams, setFilterNoTeams] = useState(_uf.filterNoTeams || false);
  const [filterNoRbacRole, setFilterNoRbacRole] = useState(_uf.filterNoRbacRole || false);
  const [filterTelnyxOnly, setFilterTelnyxOnly] = useState(_uf.filterTelnyxOnly || false);
  const [filterExcludeExempt, setFilterExcludeExempt] = useState(_uf.filterExcludeExempt || false);
  const [filterManagersOnly, setFilterManagersOnly] = useState(_uf.filterManagersOnly || false);
  const [filterNoManager, setFilterNoManager] = useState(_uf.filterNoManager || false);
  const [sortColumn, setSortColumn] = useState(_uf.sortColumn || "full_name");
  const [sortDirection, setSortDirection] = useState(_uf.sortDirection || "asc");
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  // Edit user state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedReportsTo, setSelectedReportsTo] = useState([]);
  const [reportsToSearchOpen, setReportsToSearchOpen] = useState(false);
  const [selectedTeamIds, setSelectedTeamIds] = useState([]);
  const [teamRoles, setTeamRoles] = useState({}); // { teamId: 'USER' | 'TEAM_LEADER' }
  const [selectedExempt, setSelectedExempt] = useState(false);
  const [selectedFormId, setSelectedFormId] = useState("");
  const [saving, setSaving] = useState(false);
  
  // Forms state
  const [forms, setForms] = useState([]);
  
  // Telnyx status for all users
  const [telnyxStatusMap, setTelnyxStatusMap] = useState({});
  
  // Report dialog state
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportUser, setReportUser] = useState(null);
  const [reportDays, setReportDays] = useState("30");
  const [reportFormat, setReportFormat] = useState("column");
  
  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  
  // Permissions dialog state
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [permissionsUser, setPermissionsUser] = useState(null);

  // Telnyx setup dialog state
  const [telnyxDialogOpen, setTelnyxDialogOpen] = useState(false);
  const [telnyxUser, setTelnyxUser] = useState(null);
  const [telnyxSettings, setTelnyxSettings] = useState({
    phone_number: '',
    connection_id: '',
    messaging_profile_id: '',
    webrtc_username: '',
    webrtc_password: ''
  });
  const [telnyxLoading, setTelnyxLoading] = useState(false);
  const [telnyxSaving, setTelnyxSaving] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionTestResults, setConnectionTestResults] = useState(null);
  
  // Test Call/SMS state
  const [testCallNumber, setTestCallNumber] = useState('');
  const [testSmsNumber, setTestSmsNumber] = useState('');
  const [testSmsMessage, setTestSmsMessage] = useState('');
  const [testingCall, setTestingCall] = useState(false);
  const [testingSms, setTestingSms] = useState(false);


  // HR Data state
  const [hrDataDialogOpen, setHrDataDialogOpen] = useState(false);
  const [hrDataUser, setHrDataUser] = useState(null);
  const [hrData, setHrData] = useState({});
  const [loadingHrData, setLoadingHrData] = useState(false);
  const [savingHrData, setSavingHrData] = useState(false);
  const [isHrTeamMember, setIsHrTeamMember] = useState(false);


  // Bulk selection state
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());

  // Bulk RBAC role assign state
  const [bulkRoleDialogOpen, setBulkRoleDialogOpen] = useState(false);
  const [bulkRoleId, setBulkRoleId] = useState('');
  const [bulkRoleRoles, setBulkRoleRoles] = useState([]);
  const [bulkAssigning, setBulkAssigning] = useState(false);

  // Email merge dialog state
  const [emailMergeDialogOpen, setEmailMergeDialogOpen] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sendingEmails, setSendingEmails] = useState(false);
  const [rewritingWithAi, setRewritingWithAi] = useState(false);
  const [fromEmail, setFromEmail] = useState('');
  const [connectedEmails, setConnectedEmails] = useState([]);
  
  // Login history dialog state
  const [loginHistoryDialogOpen, setLoginHistoryDialogOpen] = useState(false);
  const [loginHistoryUser, setLoginHistoryUser] = useState(null);
  const [loginHistory, setLoginHistory] = useState([]);

  // Activity Report state
  const [activityReportDialogOpen, setActivityReportDialogOpen] = useState(false);
  const [activityReportUser, setActivityReportUser] = useState(null);
  const [activityDateRange, setActivityDateRange] = useState('today');
  const [activityCustomStart, setActivityCustomStart] = useState('');
  const [activityCustomEnd, setActivityCustomEnd] = useState('');
  const [activityTypes, setActivityTypes] = useState(['all']);
  const [activityReportType, setActivityReportType] = useState('dashboard');
  const [activityReportData, setActivityReportData] = useState(null);
  const [loadingActivityReport, setLoadingActivityReport] = useState(false);

  const [loadingHistory, setLoadingHistory] = useState(false);

  // Toggle single user selection
  const toggleUserSelection = (userId) => {
    setSelectedUserIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  // Deselect all users
  const deselectAllUsers = () => {
    setSelectedUserIds(new Set());
  };

  // Get selected users data
  const getSelectedUsers = () => {
    return users.filter(u => selectedUserIds.has(u.id));
  };

  // Insert variable into email body
  const insertVariable = (variable) => {
    setEmailBody(prev => prev + `{{${variable}}}`);
  };

  const handleAiRewrite = async () => {
    if (!emailBody.trim()) {
      toast.error("Write something first, then let AI polish it");
      return;
    }
    setRewritingWithAi(true);
    try {
      const res = await api.post(`${API}/admin/ai-rewrite`, {
        text: emailBody,
        context: "email"
      });
      setEmailBody(res.data.rewritten);
      toast.success("Rewritten by AI");
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI rewrite failed");
    } finally {
      setRewritingWithAi(false);
    }
  };

  // Bulk RBAC role assign
  const handleBulkAssignRole = async () => {
    const ids = Array.from(selectedUserIds);
    if (ids.length === 0) return;
    setBulkAssigning(true);
    try {
      const res = await api.put(`${API}/rbac/users/bulk-role`, {
        user_ids: ids,
        role_id: bulkRoleId || null
      });
      toast.success(`RBAC role updated for ${res.data.updated} user(s)`);
      setBulkRoleDialogOpen(false);
      setBulkRoleId('');
      setSelectedUserIds(new Set());
      fetchData(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign role');
    } finally {
      setBulkAssigning(false);
    }
  };

  // Send bulk emails with merge
  const handleSendBulkEmails = async () => {
    const selectedUsers = getSelectedUsers();
    if (selectedUsers.length === 0) {
      toast.error('Please select at least one user');
      return;
    }
    if (!emailSubject.trim()) {
      toast.error('Please enter an email subject');
      return;
    }
    if (!emailBody.trim()) {
      toast.error('Please enter an email body');
      return;
    }

    setSendingEmails(true);
    try {
      const response = await api.post(`${API}/admin/bulk-email`, {
        user_ids: selectedUsers.map(u => u.id),
        subject: emailSubject,
        body: emailBody,
        from_email: fromEmail
      });
      
      toast.success(`Emails sent to ${response.data.sent_count} users`);
      if (response.data.failed_count > 0) {
        toast.warning(`${response.data.failed_count} emails failed to send`);
      }
      
      setEmailMergeDialogOpen(false);
      setEmailSubject('');
      setEmailBody('');
      setFromEmail('');
      setSelectedUserIds(new Set());
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send emails');
    } finally {
      setSendingEmails(false);
    }
  };

  const openPermissionsDialog = (user) => {
    setPermissionsUser(user);
    setPermissionsDialogOpen(true);
  };

  const openTelnyxDialog = async (user) => {
    setTelnyxUser(user);
    setTelnyxDialogOpen(true);
    setTelnyxLoading(true);
    // Reset test fields
    setTestCallNumber('');
    setTestSmsNumber('');
    setTestSmsMessage('');
    
    try {
      const response = await api.get(`${API}/telnyx/admin/settings/${user.id}`);
      setTelnyxSettings({
        phone_number: response.data.phone_number || '',
        connection_id: response.data.connection_id || '',
        messaging_profile_id: response.data.messaging_profile_id || '',
        webrtc_username: response.data.webrtc_username || '',
        webrtc_password: ''
      });
    } catch (error) {
      console.error('Failed to load Telnyx settings:', error);
      setTelnyxSettings({
        phone_number: '',
        connection_id: '',
        messaging_profile_id: '',
        webrtc_username: '',
        webrtc_password: ''
      });
    } finally {
      setTelnyxLoading(false);
    }
  };

  const handleSaveTelnyxSettings = async () => {
    if (!telnyxUser) return;
    
    setTelnyxSaving(true);
    try {
      await api.put(`${API}/telnyx/admin/settings/${telnyxUser.id}`, telnyxSettings);
      toast.success(`Telnyx settings saved for ${telnyxUser.full_name}`);
      
      // Update local status map
      setTelnyxStatusMap(prev => ({
        ...prev,
        [telnyxUser.id]: {
          configured: !!telnyxSettings.phone_number,
          has_phone: !!telnyxSettings.phone_number,
          has_connection: !!telnyxSettings.connection_id
        }
      }));
      
      // Invalidate cache so next refresh fetches fresh data
      cacheRef.current.timestamp = 0;
      
      setTelnyxDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save Telnyx settings');
    } finally {
      setTelnyxSaving(false);
    }
  };

  const openLoginHistoryDialog = async (user) => {
    setLoginHistoryUser(user);
    setLoginHistoryDialogOpen(true);
    setLoadingHistory(true);
    setLoginHistory([]);
    
    try {
      const response = await api.get(`${API}/auth/login-history/${user.id}`);
      setLoginHistory(response.data);
    } catch (error) {
      console.error('Failed to load login history:', error);
      toast.error('Failed to load login history');
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleTestCall = async () => {
    if (!testCallNumber.trim()) {
      toast.error('Please enter a phone number to call');
      return;
    }
    if (!telnyxUser) return;
    
    setTestingCall(true);
    try {
      await api.post(`${API}/telnyx/admin/test-call/${telnyxUser.id}`, {
        to_number: testCallNumber.trim()
      });
      toast.success(`Test call initiated to ${testCallNumber}`);
      setTestCallNumber('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate test call');
    } finally {
      setTestingCall(false);
    }
  };

  const handleTestConnection = async () => {
    if (!telnyxUser) return;
    
    setTestingConnection(true);
    setConnectionTestResults(null);
    try {
      const response = await api.post(`${API}/telnyx/admin/test-connection/${telnyxUser.id}`);
      setConnectionTestResults(response.data);
      
      if (response.data.success) {
        toast.success(response.data.summary);
      } else {
        toast.error(response.data.summary);
      }
    } catch (error) {
      console.error('Connection test error:', error);
      setConnectionTestResults({
        success: false,
        summary: 'Test failed',
        checks: [{
          name: 'Connection Test',
          status: 'error',
          message: error.response?.data?.detail || 'Failed to test connection'
        }]
      });
      toast.error('Connection test failed');
    } finally {
      setTestingConnection(false);
    }
  };


  const handleResetUserPassword = async (user) => {
    if (!(await msg.confirm('Reset password?', `Reset password for ${user.full_name}?\n\nA temporary password will be generated and sent to ${user.email}. The user will be required to change it on their next login.`))) {
      return;
    }

    try {
      const response = await api.post(`${API}/admin/reset-user-password/${user.id}`);
      toast.success(
        <div>
          <p className="font-semibold">Password Reset Successfully</p>
          <p className="text-sm mt-1">Temporary password sent to {user.email}</p>
          <p className="text-xs mt-1 font-mono bg-slate-100 p-1 rounded">Temp: {response.data.temp_password}</p>
        </div>,
        { duration: 8000 }
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const openActivityReportDialog = (user) => {
    setActivityReportUser(user);
    setActivityReportDialogOpen(true);
    setActivityReportData(null);
    setActivityDateRange('this_week');
    setActivityTypes(['all']);
    setActivityReportType('dashboard');
  };

  const handleGenerateActivityReport = async () => {
    if (!activityReportUser) return;

    setLoadingActivityReport(true);
    try {
      const response = await api.post(`${API}/activity-logs/user-report`, {
        user_id: activityReportUser.id,
        date_range: activityDateRange,
        start_date: activityCustomStart || null,
        end_date: activityCustomEnd || null,
        activity_types: activityTypes,
        report_type: activityReportType
      });
      
      setActivityReportData(response.data);
      toast.success('Activity report generated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate activity report');
    } finally {
      setLoadingActivityReport(false);
    }
  };

  const openHrDataDialog = async (user) => {
    setHrDataUser(user);
    setLoadingHrData(true);
    setHrDataDialogOpen(true);
    
    // Always fetch fresh data when opening dialog to ensure accuracy
    try {
      const response = await api.get(`${API}/hr/data/${user.id}?_t=${Date.now()}`); // Cache buster
      setHrData(response.data || {});
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Access denied. HR team members only.");
        setHrDataDialogOpen(false);
      } else {
        setHrData({});
      }
    } finally {
      setLoadingHrData(false);
    }
  };

  const handleSaveHrData = async () => {
    if (!hrDataUser) return;

    setSavingHrData(true);
    try {
      await api.put(`${API}/hr/data/${hrDataUser.id}`, hrData);
      toast.success("HR data saved successfully");
      setHrDataDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save HR data");
    } finally {
      setSavingHrData(false);
    }
  };

  const handleTestSms = async () => {
    if (!testSmsNumber.trim()) {
      toast.error('Please enter a phone number');
      return;
    }
    if (!testSmsMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }
    if (!telnyxUser) return;
    
    setTestingSms(true);
    try {
      await api.post(`${API}/telnyx/admin/test-sms/${telnyxUser.id}`, {
        to_number: testSmsNumber.trim(),
        message: testSmsMessage.trim()
      });
      toast.success(`Test SMS sent to ${testSmsNumber}`);
      setTestSmsNumber('');
      setTestSmsMessage('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test SMS');
    } finally {
      setTestingSms(false);
    }
  };

  const openReportDialog = (user) => {
    setReportUser(user);
    setReportDays("30");
    setReportFormat("column");
    setReportDialogOpen(true);
  };

  const generateReport = () => {
    if (reportUser) {
      // Open report in new tab
      const url = `/admin/user-report/${reportUser.id}?days=${reportDays}&format=${reportFormat}`;
      window.open(url, '_blank');
      setReportDialogOpen(false);
    }
  };



  // Listen for HR Data dialog open event from HoverCard
  useEffect(() => {
    const handleOpenHrData = (event) => {
      openHrDataDialog(event.detail);
    };
    
    window.addEventListener('openHrData', handleOpenHrData);
    return () => window.removeEventListener('openHrData', handleOpenHrData);
  }, []);

  useEffect(() => {
    fetchData(false); // Use cache on initial load
  }, []);

  // Load roles when bulk assign dialog opens
  useEffect(() => {
    if (bulkRoleDialogOpen && bulkRoleRoles.length === 0) {
      api.get(`${API}/rbac/roles`).then(res => setBulkRoleRoles(res.data.roles || [])).catch(() => {});
    }
  }, [bulkRoleDialogOpen]);

  const fetchData = async (forceRefresh = false) => {
    // Check if we have valid cached data
    const now = Date.now();
    const cache = cacheRef.current;
    
    if (!forceRefresh && cache.timestamp && (now - cache.timestamp) < CACHE_DURATION && cache.users) {
      // Use cached data
      setUsers(cache.users);
      setInvites(cache.invites || []);
      setTeams(cache.teams || []);
      setForms(cache.forms || []);
      setTelnyxStatusMap(cache.telnyxStatus || {});
      setLoading(false);
      return;
    }
    
    // Fetch fresh data from database
    if (forceRefresh) {
      setRefreshing(true);
    }
    
    try {
      const [usersRes, invitesRes, teamsRes, formsRes, telnyxStatusRes] = await Promise.all([
        api.get(`${API}/users`),
        api.get(`${API}/invites`),
        api.get(`${API}/teams`),
        api.get(`${API}/forms`),
        api.get(`${API}/telnyx/admin/status/all`).catch(() => ({ data: {} })),
      ]);
      // Sort users alphabetically by full_name
      const sortedUsers = usersRes.data.sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));
      const activeFormsData = formsRes.data.filter(f => f.is_active);
      
      // Update cache
      cacheRef.current = {
        users: sortedUsers,
        invites: invitesRes.data,
        teams: teamsRes.data,
        forms: activeFormsData,
        telnyxStatus: telnyxStatusRes.data || {},
        timestamp: Date.now()
      };
      
      setUsers(sortedUsers);
      setInvites(invitesRes.data);
      setTeams(teamsRes.data);
      setForms(activeFormsData);
      setTelnyxStatusMap(telnyxStatusRes.data || {});
      
      // Check if current user is in HR team
      if (currentUser) {
        const hrTeam = teamsRes.data.find(t => t.name.toLowerCase() === 'hr');
        if (hrTeam) {
          const userTeams = currentUser.team_ids || [];
          setIsHrTeamMember(userTeams.includes(hrTeam.id) || currentUser.role === 'ADMIN');
        }
      }
      
      if (forceRefresh) {
        toast.success("Data refreshed from database");
      }
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }

  };
  
  // Reset filters and show cached data
  const handleReset = () => {
    setSearchQuery("");
    setFilterRole("all");
    setFilterStatus("all");
    setFilterExempt("all");
    setFilterTeam("all");
    setFilterManager("all");
    setFilterManagersOnly(false);
    setFilterNoManager(false);
    setFilterNoTeams(false);
    setFilterTelnyxOnly(false);
    setFilterExcludeExempt(false);
    setCurrentPage(1);
    sessionStorage.removeItem('jtcrm_users_filters');
    toast.success("Filters cleared");
  };

  useEffect(() => {
    sessionStorage.setItem('jtcrm_users_filters', JSON.stringify({
      searchQuery, filterRole, filterStatus, filterExempt, filterTeam, filterManager,
      filterNoTeams, filterNoRbacRole, filterTelnyxOnly, filterExcludeExempt,
      filterManagersOnly, filterNoManager, sortColumn, sortDirection,
    }));
  }, [searchQuery, filterRole, filterStatus, filterExempt, filterTeam, filterManager,
    filterNoTeams, filterNoRbacRole, filterTelnyxOnly, filterExcludeExempt,
    filterManagersOnly, filterNoManager, sortColumn, sortDirection]);
  
  // Force refresh data from database
  const handleRefresh = () => {
    fetchData(true);
  };

  const handleCreateInvite = async () => {
    setCreating(true);
    try {
      const response = await api.post(`${API}/invites/create`, {
        intended_email: isReusable ? null : (intendedEmail || null),
        is_reusable: isReusable,
      });
      setNewInviteUrl(response.data.setup_url);
      toast.success("Invite created successfully!");
      fetchData(true); // Force refresh after creating invite
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create invite");
    } finally {
      setCreating(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(newInviteUrl);
    toast.success("Link copied to clipboard!");
  };

  // Admins can flip any user's status; team leaders only their direct reports.
  // Mirrors the backend scoping in routes/users.py:_can_manage_user_status.
  const canManageUserStatus = (target) => {
    if (!currentUser || target.id === currentUser.id) return false;
    if (currentUser.role === "ADMIN") return true;
    if (currentUser.role !== "TEAM_LEADER") return false;
    const rt = target.reports_to;
    const ids = Array.isArray(rt) ? rt : (rt ? [rt] : []);
    return ids.includes(currentUser.id);
  };

  const handleToggleUserStatus = async (userId, isActive) => {
    try {
      const endpoint = isActive ? "deactivate" : "activate";
      await api.post(`${API}/users/${userId}/${endpoint}`);
      toast.success(`User ${isActive ? "deactivated" : "activated"}`);
      fetchData(true); // Force refresh
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    }
  };

  const handleRevokeInvite = async (inviteId) => {
    try {
      await api.post(`${API}/invites/${inviteId}/revoke`);
      toast.success("Invite revoked");
      fetchData(true); // Force refresh
    } catch (error) {
      toast.error("Failed to revoke invite");
    }
  };

  const handleDeleteInvite = async (inviteId) => {
    try {
      await api.delete(`${API}/invites/${inviteId}`);
      toast.success("Invite deleted");
      fetchData(true); // Force refresh
    } catch (error) {
      toast.error("Failed to delete invite");
    }
  };

  const handleCopyInviteLink = async (setupUrl) => {
    if (setupUrl) {
      try {
        await navigator.clipboard.writeText(setupUrl);
        toast.success("Link copied to clipboard!");
      } catch (err) {
        // Fallback for browsers that don't support clipboard API
        const textArea = document.createElement("textarea");
        textArea.value = setupUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        toast.success("Link copied to clipboard!");
      }
    } else {
      toast.error("Link not available - this invite was created before this feature");
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await api.delete(`${API}/users/${userId}`);
      toast.success("User deleted successfully");
      fetchData(true); // Force refresh
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setSelectedRole(user.role);
    const rt = user.reports_to;
    setSelectedReportsTo(Array.isArray(rt) ? rt : (rt ? [rt] : []));
    setSelectedTeamIds(user.team_ids || []);
    setSelectedExempt(user.is_exempt || false);
    setSelectedFormId(user.assigned_form_id || "");
    
    // Initialize team roles from user data or default to USER
    const initialTeamRoles = {};
    (user.team_ids || []).forEach(teamId => {
      // Check if user has team_roles data, otherwise use global role or default
      const teamRole = user.team_roles?.[teamId] || (user.role === 'TEAM_LEADER' ? 'TEAM_LEADER' : 'USER');
      initialTeamRoles[teamId] = teamRole;
    });
    setTeamRoles(initialTeamRoles);
    
    setEditDialogOpen(true);
  };

  const handleTeamToggle = (teamId) => {
    setSelectedTeamIds(prev => {
      if (prev.includes(teamId)) {
        // Remove team - also remove from teamRoles
        const newRoles = { ...teamRoles };
        delete newRoles[teamId];
        setTeamRoles(newRoles);
        return prev.filter(id => id !== teamId);
      } else {
        // Add team - set default role
        setTeamRoles(prev => ({ ...prev, [teamId]: 'USER' }));
        return [...prev, teamId];
      }
    });
  };

  const handleTeamRoleChange = (teamId, role) => {
    setTeamRoles(prev => ({ ...prev, [teamId]: role }));
  };

  const handleSaveUserChanges = async () => {
    setSaving(true);
    try {
      // Update role if changed (this is the default/global role)
      if (selectedRole !== selectedUser.role) {
        await api.put(`${API}/users/${selectedUser.id}/role`, { role: selectedRole });
      }
      
      // Update reports_to if changed
      const currentRT = Array.isArray(selectedUser.reports_to) ? selectedUser.reports_to : (selectedUser.reports_to ? [selectedUser.reports_to] : []);
      const newRT = selectedReportsTo || [];
      if (JSON.stringify(newRT.sort()) !== JSON.stringify([...currentRT].sort())) {
        await api.put(`${API}/users/${selectedUser.id}`, { reports_to: newRT });
      }
      
      // Update teams and team roles if changed
      const currentTeamIds = selectedUser.team_ids || [];
      const teamsChanged = JSON.stringify(selectedTeamIds.sort()) !== JSON.stringify(currentTeamIds.sort());
      const rolesChanged = JSON.stringify(teamRoles) !== JSON.stringify(selectedUser.team_roles || {});
      
      if (teamsChanged || rolesChanged) {
        await api.put(`${API}/users/${selectedUser.id}/teams`, { 
          team_ids: selectedTeamIds,
          team_roles: teamRoles
        });
      }
      
      // Update exempt status if changed
      const currentExempt = selectedUser.is_exempt || false;
      if (selectedExempt !== currentExempt) {
        await api.put(`${API}/users/${selectedUser.id}/exempt`, { is_exempt: selectedExempt });
      }
      
      // Update assigned form if changed
      const currentFormId = selectedUser.assigned_form_id || "";
      if (selectedFormId !== currentFormId) {
        await api.put(`${API}/users/${selectedUser.id}/assign-form?form_id=${selectedFormId || ''}`);
      }
      
      toast.success("User updated successfully!");
      setEditDialogOpen(false);
      fetchData(true); // Force refresh
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    } finally {
      setSaving(false);
    }
  };

  // Deactivate user (disables login but keeps data for reports)
  const handleDeactivateUser = async () => {
    if (!selectedUser) return;
    
    setDeactivating(true);
    try {
      await api.post(`${API}/users/${selectedUser.id}/deactivate`);
      toast.success(`${selectedUser.full_name} has been deactivated`);
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to deactivate user");
    } finally {
      setDeactivating(false);
    }
  };

  // Reactivate user
  const handleReactivateUser = async () => {
    if (!selectedUser) return;
    
    setDeactivating(true);
    try {
      await api.post(`${API}/users/${selectedUser.id}/reactivate`);
      toast.success(`${selectedUser.full_name} has been reactivated`);
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reactivate user");
    } finally {
      setDeactivating(false);
    }
  };

  // Open delete confirmation dialog
  const openDeleteDialog = () => {
    setUserToDelete(selectedUser);
    setDeleteDialogOpen(true);
  };

  // Delete user and all their data (from Edit dialog with confirmation)
  const handleConfirmDeleteUser = async () => {
    if (!userToDelete) return;
    
    setDeleting(true);
    try {
      await api.delete(`${API}/users/${userToDelete.id}`);
      toast.success(`${userToDelete.full_name} and all their data have been deleted`);
      setDeleteDialogOpen(false);
      setEditDialogOpen(false);
      setUserToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    } finally {
      setDeleting(false);
    }
  };

  const resetInviteDialog = () => {
    setIntendedEmail("");
    setIsReusable(false);
    setNewInviteUrl("");
    setInviteDialogOpen(false);
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case "ADMIN": return "bg-purple-100 text-purple-700 border-purple-200";
      case "TEAM_LEADER": return "bg-blue-100 text-blue-700 border-blue-200";
      default: return "";
    }
  };

  // Get unique managers from users (users who have direct reports)
  const managers = useMemo(() => {
    const managerIds = new Set();
    users.forEach(user => {
      const rt = user.reports_to;
      const ids = Array.isArray(rt) ? rt : (rt ? [rt] : []);
      ids.forEach(id => { if (id) managerIds.add(id); });
    });

    // Get manager details
    const managerList = Array.from(managerIds)
      .map(managerId => users.find(u => u.id === managerId))
      .filter(Boolean)
      .sort((a, b) => (a.full_name || "").localeCompare(b.full_name || ""));

    return managerList;
  }, [users]);

  // Sort handler
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  // Get sort icon
  const SortIcon = ({ column }) => {
    if (sortColumn !== column) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-50" />;
    return sortDirection === "asc" 
      ? <ArrowUp className="h-3 w-3 ml-1" /> 
      : <ArrowDown className="h-3 w-3 ml-1" />;
  };

  // Filtered and sorted users
  const filteredUsers = useMemo(() => {
    let result = [...users];
    
    // Search filter (name or email)
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(u => 
        u.full_name?.toLowerCase().includes(query) || 
        u.email?.toLowerCase().includes(query)
      );
    }
    
    // Role filter
    if (filterRole !== "all") {
      result = result.filter(u => u.role === filterRole);
    }
    
    // Status filter
    if (filterStatus !== "all") {
      result = result.filter(u => filterStatus === "active" ? u.is_active : !u.is_active);
    }
    
    // Exempt filter
    if (filterExempt !== "all") {
      result = result.filter(u => filterExempt === "yes" ? u.is_exempt : !u.is_exempt);
    }
    
    // Team filter
    if (filterTeam !== "all") {
      result = result.filter(u => u.team_ids?.includes(filterTeam));
    }
    
    // Manager filter
    if (filterManager !== "all") {
      result = result.filter(u => {
        const rt = u.reports_to;
        const ids = Array.isArray(rt) ? rt : (rt ? [rt] : []);
        return ids.includes(filterManager);
      });
    }
    
    // No teams filter
    if (filterNoTeams) {
      result = result.filter(u => !u.team_ids || u.team_ids.length === 0);
    }

    // No RBAC role filter
    if (filterNoRbacRole) {
      result = result.filter(u => !u.role_name);
    }
    
    // Telnyx configured filter
    if (filterTelnyxOnly) {
      result = result.filter(u => telnyxStatusMap[u.id]?.configured);
    }
    
    // Exclude exempt users filter
    if (filterExcludeExempt) {
      result = result.filter(u => !u.is_exempt);
    }

    // Managers only filter
    if (filterManagersOnly) {
      const managerIdSet = new Set();
      users.forEach(u => {
        const rt = u.reports_to;
        const ids = Array.isArray(rt) ? rt : (rt ? [rt] : []);
        ids.forEach(id => { if (id) managerIdSet.add(id); });
      });
      result = result.filter(u => managerIdSet.has(u.id));
    }

    // No manager assigned filter
    if (filterNoManager) {
      result = result.filter(u => {
        const rt = u.reports_to;
        return !rt || (Array.isArray(rt) && rt.length === 0);
      });
    }
    
    // Sort
    result.sort((a, b) => {
      let aVal, bVal;
      
      switch (sortColumn) {
        case "full_name":
          aVal = a.full_name?.toLowerCase() || "";
          bVal = b.full_name?.toLowerCase() || "";
          break;
        case "email":
          aVal = a.email?.toLowerCase() || "";
          bVal = b.email?.toLowerCase() || "";
          break;
        case "role":
          aVal = a.role || "";
          bVal = b.role || "";
          break;
        case "is_active":
          aVal = a.is_active ? 1 : 0;
          bVal = b.is_active ? 1 : 0;
          break;
        case "is_exempt":
          aVal = a.is_exempt ? 1 : 0;
          bVal = b.is_exempt ? 1 : 0;
          break;
        case "created_at":
          aVal = a.created_at || "";
          bVal = b.created_at || "";
          break;
        default:
          aVal = "";
          bVal = "";
      }
      
      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
    
    return result;
  }, [users, searchQuery, filterRole, filterStatus, filterExempt, filterTeam, filterManager, filterNoTeams, filterNoRbacRole, filterTelnyxOnly, filterExcludeExempt, filterManagersOnly, filterNoManager, telnyxStatusMap, sortColumn, sortDirection]);

  // Select all filtered users (on current page or all)
  const selectAllUsers = () => {
    const allFilteredIds = filteredUsers.map(u => u.id);
    setSelectedUserIds(new Set(allFilteredIds));
  };

  // Check if all filtered users are selected
  const allFilteredSelected = filteredUsers.length > 0 && 
    filteredUsers.every(u => selectedUserIds.has(u.id));

  // Pagination calculations
  const totalPages = Math.ceil(filteredUsers.length / pageSize);
  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredUsers.slice(startIndex, startIndex + pageSize);
  }, [filteredUsers, currentPage, pageSize]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterRole, filterStatus, filterExempt, filterTeam, filterManager, filterManagersOnly, filterNoManager, filterNoTeams, filterNoRbacRole, filterTelnyxOnly, filterExcludeExempt, pageSize]);

  const activeUsers = users.filter((u) => u.is_active);
  const teamLeaders = users.filter(u => u.role === "TEAM_LEADER");
  const pendingInvites = invites.filter((i) => {
    // For reusable invites, just check expiry
    if (i.is_reusable) {
      return new Date(i.expires_at) > new Date();
    }
    // For single-use invites, check both used_at and expiry
    return !i.used_at && new Date(i.expires_at) > new Date();
  });
  
  // All active (non-expired, non-used) invites for the tab
  const activeInvites = invites.filter((i) => {
    if (i.is_reusable) {
      return new Date(i.expires_at) > new Date();
    }
    return !i.used_at && new Date(i.expires_at) > new Date();
  });

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            User Management
          </h1>
          <p className="text-slate-600 mt-1">
            Manage team members, roles, and invites
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Selection info and email button */}
          {selectedUserIds.size > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600">
                {selectedUserIds.size} selected
              </span>
              <Button 
                variant="outline" 
                size="sm"
                onClick={deselectAllUsers}
              >
                <X className="h-3 w-3 mr-1" />
                Clear
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setBulkRoleDialogOpen(true)}
                className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
              >
                <Shield className="h-3 w-3 mr-1" />
                Assign Role
              </Button>
              <Button
                data-testid="email-selected-users"
                onClick={async () => {
                  setEmailMergeDialogOpen(true);
                  try {
                    const res = await api.get(`${API}/email-accounts`);
                    const emails = (res.data || []).filter(a => a.provider === 'office365').map(a => a.email);
                    setConnectedEmails(emails);
                    setFromEmail(emails[0] || 'no-reply@jobtalk.ai');
                  } catch {
                    setConnectedEmails([]);
                    setFromEmail('no-reply@jobtalk.ai');
                  }
                }}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Mail className="mr-2 h-4 w-4" />
                Email Selected
              </Button>
            </div>
          )}

          <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-invite-button">
                <UserPlus className="mr-2 h-4 w-4" />
                Create Invite
              </Button>
            </DialogTrigger>
          <DialogContent>
            {newInviteUrl ? (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    Invite Created!
                  </DialogTitle>
                  <DialogDescription>
                    Share this link with the user to set up their account.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                    <LinkIcon className="h-4 w-4 text-slate-400 shrink-0" />
                    <code className="text-sm break-all flex-1">{newInviteUrl}</code>
                  </div>
                  <Button onClick={handleCopyLink} className="w-full" data-testid="copy-invite-link-button">
                    <Copy className="mr-2 h-4 w-4" />
                    Copy Link
                  </Button>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={resetInviteDialog}>
                    Done
                  </Button>
                </DialogFooter>
              </>
            ) : (
              <>
                <DialogHeader>
                  <DialogTitle>Create New Invite</DialogTitle>
                  <DialogDescription>
                    Generate a setup link for new team members.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  {/* Reusable Toggle */}
                  <div className="flex items-center space-x-2 p-3 bg-slate-50 rounded-lg">
                    <Checkbox
                      id="isReusable"
                      checked={isReusable}
                      onCheckedChange={setIsReusable}
                      data-testid="reusable-checkbox"
                    />
                    <div className="flex-1">
                      <label
                        htmlFor="isReusable"
                        className="text-sm font-medium leading-none cursor-pointer"
                      >
                        Reusable Link (Multiple Users)
                      </label>
                      <p className="text-xs text-slate-500 mt-1">
                        Create a generic link that multiple team members can use to sign up
                      </p>
                    </div>
                  </div>

                  {/* Email field - only show for single-use invites */}
                  {!isReusable && (
                    <div className="space-y-2">
                      <Label htmlFor="intendedEmail">Intended Email (Optional)</Label>
                      <Input
                        id="intendedEmail"
                        type="email"
                        placeholder="user@company.com"
                        value={intendedEmail}
                        onChange={(e) => setIntendedEmail(e.target.value)}
                        data-testid="invite-email-input"
                      />
                      <p className="text-xs text-slate-500">
                        If specified, only this email can use the invite link.
                      </p>
                    </div>
                  )}

                  {isReusable && (
                    <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg">
                      <p className="text-sm text-blue-700">
                        <strong>Reusable Link:</strong> Anyone with this link can create an account. 
                        Share it with your team via email, Slack, or any channel.
                      </p>
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setInviteDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleCreateInvite} disabled={creating} data-testid="generate-invite-button">
                    {creating ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      isReusable ? "Generate Reusable Link" : "Generate Link"
                    )}
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{users.length}</p>
                <p className="text-sm text-slate-500">Total Users</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{activeUsers.length}</p>
                <p className="text-sm text-slate-500">Active Users</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Team Leaders with Hover */}
        <HoverCard openDelay={100} closeDelay={100}>
          <HoverCardTrigger asChild>
            <Card className="cursor-pointer hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Shield className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{teamLeaders.length}</p>
                    <p className="text-sm text-slate-500">Team Leaders</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </HoverCardTrigger>
          <HoverCardContent className="w-80 max-h-[300px] overflow-y-auto">
            <div className="flex items-center gap-2 mb-3 pb-2 border-b">
              <Shield className="h-4 w-4 text-blue-600" />
              <span className="font-semibold">Team Leaders ({teamLeaders.length})</span>
            </div>
            {teamLeaders.length > 0 ? (
              <div className="space-y-2">
                {teamLeaders.map((user) => (
                  <div key={user.id} className="flex items-center justify-between p-2 bg-blue-50 rounded-lg text-sm">
                    <div>
                      <p className="font-medium text-slate-800">{user.full_name}</p>
                      <p className="text-xs text-slate-500">{user.email}</p>
                      {user.team_names && user.team_names.length > 0 && (
                        <p className="text-xs text-blue-600 mt-1">
                          Teams: {user.team_names.join(", ")}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-2 text-sm">No team leaders assigned yet</p>
            )}
          </HoverCardContent>
        </HoverCard>

        {/* Pending Invites with Hover */}
        <HoverCard openDelay={100} closeDelay={100}>
          <HoverCardTrigger asChild>
            <Card className="cursor-pointer hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                    <LinkIcon className="h-6 w-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{pendingInvites.length}</p>
                    <p className="text-sm text-slate-500">Pending Invites</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </HoverCardTrigger>
          <HoverCardContent className="w-80 max-h-[300px] overflow-y-auto">
            <div className="flex items-center gap-2 mb-3 pb-2 border-b">
              <LinkIcon className="h-4 w-4 text-amber-600" />
              <span className="font-semibold">Active Invite Links ({pendingInvites.length})</span>
            </div>
            {pendingInvites.length > 0 ? (
              <div className="space-y-2">
                {pendingInvites.map((invite) => (
                  <div key={invite.id} className="p-2 bg-amber-50 rounded-lg text-sm">
                    <div className="flex items-center justify-between mb-1">
                      <Badge variant="outline" className={invite.is_reusable ? "bg-blue-50 text-blue-700 border-blue-200 text-xs" : "text-xs"}>
                        {invite.is_reusable ? "Reusable" : "Single-use"}
                      </Badge>
                      <span className="text-xs text-slate-500">
                        Expires {fmtDateEST(invite.expires_at)}
                      </span>
                    </div>
                    <p className="text-xs text-slate-600">
                      {invite.is_reusable 
                        ? `${invite.use_count || 0} signups so far` 
                        : (invite.intended_email || "Any email")}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-2 text-sm">No pending invites</p>
            )}
          </HoverCardContent>
        </HoverCard>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <Tabs defaultValue="members" className="w-full">
          <TabsList className="grid w-full max-w-lg grid-cols-3">
            <TabsTrigger value="members" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Team Members
              <Badge variant="secondary" className="ml-1 text-xs">{users.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="invites" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Active Invites
              <Badge variant="secondary" className="ml-1 text-xs">{activeInvites.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="roles" className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Roles
            </TabsTrigger>
          </TabsList>
          
          {/* Team Members Tab */}
          <TabsContent value="members" className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <CardTitle>Team Members</CardTitle>
                    <CardDescription>
                      {filteredUsers.length} of {users.length} users
                    </CardDescription>
                  </div>
                </div>
                
                {/* Search and Filters */}
                <div className="mt-4 space-y-4">
                  {/* Search with Reset and Refresh icons */}
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        placeholder="Search by name or email..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 pr-10"
                        data-testid="user-search-input"
                      />
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery("")}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                          title="Clear search"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleReset}
                      title="Reset all filters (use cached data)"
                      className="shrink-0"
                      data-testid="reset-filters-btn"
                    >
                      <RotateCcw className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleRefresh}
                      disabled={refreshing}
                      title="Refresh data from database"
                      className="shrink-0"
                      data-testid="refresh-data-btn"
                    >
                      {refreshing ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  
                  {/* Filters */}
                  <div className="flex flex-wrap gap-2">
                    <Select value={filterRole} onValueChange={setFilterRole}>
                      <SelectTrigger className="w-[140px]">
                        <SelectValue placeholder="Role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Roles</SelectItem>
                        <SelectItem value="ADMIN">Admin</SelectItem>
                        <SelectItem value="TEAM_LEADER">Team Leader</SelectItem>
                        <SelectItem value="USER">User</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                      <SelectTrigger className="w-[130px]">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <Select value={filterExempt} onValueChange={setFilterExempt}>
                      <SelectTrigger className="w-[130px]">
                        <SelectValue placeholder="Exempt" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="yes">Exempt</SelectItem>
                        <SelectItem value="no">Not Exempt</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <Select value={filterTeam} onValueChange={setFilterTeam}>
                      <SelectTrigger className="w-[150px]">
                        <SelectValue placeholder="Team" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Teams</SelectItem>
                        {teams.map((team) => (
                          <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    <Select value={filterManager} onValueChange={setFilterManager}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Manager" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Managers</SelectItem>
                        {managers.map((manager) => (
                          <SelectItem key={manager.id} value={manager.id}>{manager.full_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    {/* Managers Only Filter */}
                    <div className="flex items-center space-x-2 px-3 py-2 border rounded-md bg-white">
                      <Checkbox
                        id="filterManagersOnly"
                        checked={filterManagersOnly}
                        onCheckedChange={setFilterManagersOnly}
                        data-testid="filter-managers-only"
                      />
                      <label
                        htmlFor="filterManagersOnly"
                        className="text-sm font-medium leading-none cursor-pointer whitespace-nowrap flex items-center gap-1"
                      >
                        <Shield className="h-3 w-3 text-purple-600" />
                        Managers Only
                      </label>
                    </div>

                    {/* No Manager Filter */}
                    <div className="flex items-center space-x-2 px-3 py-2 border rounded-md bg-white">
                      <Checkbox
                        id="filterNoManager"
                        checked={filterNoManager}
                        onCheckedChange={setFilterNoManager}
                      />
                      <label
                        htmlFor="filterNoManager"
                        className="text-sm font-medium leading-none cursor-pointer whitespace-nowrap flex items-center gap-1"
                      >
                        <UserX className="h-3 w-3 text-orange-600" />
                        No Manager
                      </label>
                    </div>

                    {/* No Teams Filter Toggle */}
                    <div className="flex items-center space-x-2 px-3 py-2 border rounded-md bg-white">
                      <Checkbox
                        id="filterNoTeams"
                        checked={filterNoTeams}
                        onCheckedChange={setFilterNoTeams}
                        data-testid="filter-no-teams"
                      />
                      <label
                        htmlFor="filterNoTeams"
                        className="text-sm font-medium leading-none cursor-pointer whitespace-nowrap"
                      >
                        No Teams Only
                      </label>
                    </div>

                    {/* No RBAC Role Filter */}
                    <div className="flex items-center space-x-2 px-3 py-2 border rounded-md bg-white">
                      <Checkbox
                        id="filterNoRbacRole"
                        checked={filterNoRbacRole}
                        onCheckedChange={setFilterNoRbacRole}
                        data-testid="filter-no-rbac-role"
                      />
                      <label
                        htmlFor="filterNoRbacRole"
                        className="text-sm font-medium leading-none cursor-pointer whitespace-nowrap flex items-center gap-1"
                      >
                        <Shield className="h-3 w-3 text-red-500" />
                        No RBAC Role
                      </label>
                    </div>

                    {/* Telnyx Filter */}
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="filterTelnyx"
                        checked={filterTelnyxOnly}
                        onCheckedChange={setFilterTelnyxOnly}
                        data-testid="filter-telnyx"
                      />
                      <label
                        htmlFor="filterTelnyx"
                        className="text-sm font-medium leading-none cursor-pointer whitespace-nowrap flex items-center gap-1"
                      >
                        <Phone className="h-3 w-3 text-green-600" />
                        TelNyx
                      </label>
                    </div>
                    
                    {/* Exclude Exempt Filter */}
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="filterExcludeExempt"
                        checked={filterExcludeExempt}
                        onCheckedChange={setFilterExcludeExempt}
                        data-testid="filter-exclude-exempt"
                      />
                      <label
                        htmlFor="filterExcludeExempt"
                        className="text-sm font-medium leading-none cursor-pointer whitespace-nowrap"
                      >
                        Exclude Exempt
                      </label>
                    </div>
                    
                    {(searchQuery || filterRole !== "all" || filterStatus !== "all" || filterExempt !== "all" || filterTeam !== "all" || filterManager !== "all" || filterNoTeams || filterNoRbacRole || filterTelnyxOnly || filterExcludeExempt || filterManagersOnly || filterNoManager) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSearchQuery("");
                          setFilterRole("all");
                          setFilterStatus("all");
                          setFilterExempt("all");
                          setFilterTeam("all");
                          setFilterManager("all");
                          setFilterManagersOnly(false);
                          setFilterNoManager(false);
                          setFilterNoTeams(false);
                          setFilterNoRbacRole(false);
                          setFilterTelnyxOnly(false);
                          setFilterExcludeExempt(false);
                        }}
                      >
                        Clear filters
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full data-table">
                    <thead>
                      <tr>
                        <th className="w-10">
                          <Checkbox
                            checked={allFilteredSelected}
                            onCheckedChange={(checked) => checked ? selectAllUsers() : deselectAllUsers()}
                            data-testid="select-all-users"
                          />
                        </th>
                        <th 
                          className="cursor-pointer hover:bg-slate-100 select-none"
                          onClick={() => handleSort("full_name")}
                        >
                          <div className="flex items-center">
                            Name <SortIcon column="full_name" />
                          </div>
                        </th>
                        <th 
                          className="cursor-pointer hover:bg-slate-100 select-none"
                          onClick={() => handleSort("email")}
                        >
                          <div className="flex items-center">
                            Email <SortIcon column="email" />
                          </div>
                        </th>
                        <th>Teams</th>
                        <th
                          className="cursor-pointer hover:bg-slate-100 select-none"
                          onClick={() => handleSort("role")}
                        >
                          <div className="flex items-center">
                            Role <SortIcon column="role" />
                          </div>
                        </th>
                        <th>RBAC Role</th>
                        <th
                          className="cursor-pointer hover:bg-slate-100 select-none"
                          onClick={() => handleSort("is_exempt")}
                        >
                          <div className="flex items-center">
                            Exempt <SortIcon column="is_exempt" />
                          </div>
                        </th>
                        <th 
                          className="cursor-pointer hover:bg-slate-100 select-none"
                          onClick={() => handleSort("is_active")}
                        >
                          <div className="flex items-center">
                            Status <SortIcon column="is_active" />
                          </div>
                        </th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedUsers.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="text-center py-8 text-slate-500">
                            No users found matching your filters
                          </td>
                        </tr>
                      ) : (
                        paginatedUsers.map((user) => (
                          <tr key={user.id} data-testid={`user-row-${user.id}`}>
                            <td className="w-10">
                              <Checkbox
                                checked={selectedUserIds.has(user.id)}
                                onCheckedChange={() => toggleUserSelection(user.id)}
                                data-testid={`select-user-${user.id}`}
                              />
                            </td>
                            <td className="font-medium">
                              <button
                                onClick={() => openReportDialog(user)}
                                className="text-left text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                                title="Click to generate log report"
                              >
                                {user.full_name}
                              </button>
                            </td>
                            <td>{user.email}</td>
                            <td>
                              {user.team_names && user.team_names.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {user.team_names.map((name, idx) => (
                                    <Badge key={idx} variant="outline" className="bg-slate-50 text-xs">
                                      {name}
                                    </Badge>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </td>
                            <td>
                              <Badge variant="outline" className={getRoleBadgeClass(user.role)}>
                                {user.role === "TEAM_LEADER" ? "Team Leader" : user.role}
                              </Badge>
                            </td>
                            <td>
                              {user.role_name ? (
                                <Badge
                                  variant="outline"
                                  className="bg-indigo-50 text-indigo-700 border-indigo-200 text-xs cursor-pointer hover:bg-indigo-100"
                                  onClick={() => openPermissionsDialog(user)}
                                  title="Click to manage permissions"
                                >
                                  <Shield className="h-3 w-3 mr-1" />
                                  {user.role_name}
                                </Badge>
                              ) : (
                                <Badge
                                  variant="outline"
                                  className="bg-slate-50 text-slate-400 border-slate-200 text-xs cursor-pointer hover:bg-slate-100"
                                  onClick={() => openPermissionsDialog(user)}
                                  title="Click to assign a role"
                                >
                                  No Role
                                </Badge>
                              )}
                            </td>
                            <td>
                              {user.is_exempt ? (
                                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                                  Yes
                                </Badge>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                          <td>
                              {user.is_active ? (
                                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                  Active
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                                  Inactive
                                </Badge>
                              )}
                          </td>
                          <td>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditDialog(user)}
                                data-testid={`edit-user-${user.id}`}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openPermissionsDialog(user)}
                                data-testid={`permissions-user-${user.id}`}
                                title="Manage Permissions"
                              >
                                <Key className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openTelnyxDialog(user)}
                                data-testid={`telnyx-user-${user.id}`}
                                title={telnyxStatusMap[user.id]?.configured ? "Telnyx Configured - Click to Edit" : "Setup Telnyx"}
                                className="text-green-600 hover:text-green-700 hover:bg-green-50 relative"
                              >
                                <Phone className="h-4 w-4" />
                                {telnyxStatusMap[user.id]?.configured && (
                                  <CheckCircle className="h-3 w-3 text-green-600 absolute -top-0.5 -right-0.5 bg-white rounded-full" />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openLoginHistoryDialog(user)}
                                data-testid={`login-history-${user.id}`}
                                title="View Login History"
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              >
                                <History className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openActivityReportDialog(user)}
                                data-testid={`activity-report-${user.id}`}
                                title="Activity Report"
                                className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                              >
                                <FileText className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleResetUserPassword(user)}
                                data-testid={`reset-password-${user.id}`}
                                title="Reset Password"
                                className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                              >
                                <RotateCcw className="h-4 w-4" />
                              </Button>
                              {canManageUserStatus(user) && (
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    {user.is_active ? (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        data-testid={`deactivate-user-${user.id}`}
                                        title="Mark as terminated (stops daily-log emails and notifications)"
                                        className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                      >
                                        <UserX className="h-4 w-4" />
                                      </Button>
                                    ) : (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        data-testid={`activate-user-${user.id}`}
                                        title="Reactivate employee"
                                        className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                      >
                                        <UserCheck className="h-4 w-4" />
                                      </Button>
                                    )}
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>
                                        {user.is_active ? `Mark ${user.full_name} as terminated?` : `Reactivate ${user.full_name}?`}
                                      </AlertDialogTitle>
                                      <AlertDialogDescription>
                                        {user.is_active
                                          ? "They will stop receiving daily-log reminder emails, task/meeting notifications, and daily digests, and will be hidden from the All Logs view. Historical data is preserved and they can be reactivated later."
                                          : "They will resume receiving reminders and notifications, and reappear in the All Logs view."}
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() => handleToggleUserStatus(user.id, user.is_active)}
                                        className={user.is_active ? "bg-amber-600 hover:bg-amber-700" : "bg-green-600 hover:bg-green-700"}
                                      >
                                        {user.is_active ? "Mark as terminated" : "Reactivate"}
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              )}
                              {isHrTeamMember && (
                                <HRDataHoverCard user={user} />
                              )}
                              {user.role !== "ADMIN" && (
                                <>
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                        data-testid={`delete-user-${user.id}`}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                      <AlertDialogHeader>
                                        <AlertDialogTitle>Delete {user.full_name}?</AlertDialogTitle>
                                        <AlertDialogDescription>
                                          This will permanently delete this user and all their logs. This action cannot be undone.
                                        </AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                                        <AlertDialogAction
                                          onClick={() => handleDeleteUser(user.id)}
                                          className="bg-red-600 hover:bg-red-700"
                                        >
                                          Delete
                                        </AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                      )}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination Controls */}
                {filteredUsers.length > 0 && (
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4 pt-4 border-t">
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <span>Showing</span>
                      <Select value={pageSize.toString()} onValueChange={(v) => setPageSize(parseInt(v))}>
                        <SelectTrigger className="w-[70px] h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="5">5</SelectItem>
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="20">20</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                          <SelectItem value="100">100</SelectItem>
                        </SelectContent>
                      </Select>
                      <span>of {filteredUsers.length} users</span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                      >
                        First
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </Button>
                      <span className="text-sm text-slate-600 px-2">
                        Page {currentPage} of {totalPages || 1}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage >= totalPages}
                      >
                        Next
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage >= totalPages}
                      >
                        Last
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Active Invites Tab */}
          <TabsContent value="invites" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Active Invites</CardTitle>
                <CardDescription>
                  Invite links that can still be used to sign up
                </CardDescription>
              </CardHeader>
              <CardContent>
                {activeInvites.length === 0 ? (
                  <div className="text-center py-12">
                    <LinkIcon className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-slate-600 mb-2">No Active Invites</h3>
                    <p className="text-slate-500 mb-4">Create an invite link to add new team members</p>
                    <Button onClick={() => setInviteDialogOpen(true)}>
                      <UserPlus className="mr-2 h-4 w-4" />
                      Create Invite
                    </Button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full data-table">
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th>Email / Uses</th>
                          <th>Created</th>
                          <th>Expires</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activeInvites.map((invite) => (
                          <tr key={invite.id} data-testid={`invite-row-${invite.id}`}>
                            <td>
                              {invite.is_reusable ? (
                                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                  Reusable
                                </Badge>
                              ) : (
                                <Badge variant="outline">Single-use</Badge>
                              )}
                            </td>
                            <td>
                              {invite.is_reusable ? (
                                <span className="text-slate-600">{invite.use_count || 0} signups</span>
                              ) : (
                                invite.intended_email || <span className="text-slate-400">Any email</span>
                              )}
                            </td>
                            <td className="text-slate-500">
                              {fmtDateTimeEST(invite.created_at)}
                            </td>
                            <td className="text-slate-500">
                              {fmtDateTimeEST(invite.expires_at)}
                            </td>
                            <td>
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                  onClick={() => handleCopyInviteLink(invite.setup_url)}
                                  data-testid={`copy-invite-${invite.id}`}
                                  title="Copy Link"
                                >
                                  <Copy className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => handleDeleteInvite(invite.id)}
                                  data-testid={`delete-invite-${invite.id}`}
                                  title="Delete Link"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Roles Tab */}
          <TabsContent value="roles" className="mt-6">
            <Card>
              <CardContent className="pt-6">
                <RolesManagement />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* Edit User Dialog */}
      <EditUserDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        selectedUser={selectedUser}
        selectedRole={selectedRole}
        setSelectedRole={setSelectedRole}
        selectedReportsTo={selectedReportsTo}
        setSelectedReportsTo={setSelectedReportsTo}
        reportsToSearchOpen={reportsToSearchOpen}
        setReportsToSearchOpen={setReportsToSearchOpen}
        users={users}
        selectedTeamIds={selectedTeamIds}
        setSelectedTeamIds={setSelectedTeamIds}
        teamRoles={teamRoles}
        handleTeamToggle={handleTeamToggle}
        handleTeamRoleChange={handleTeamRoleChange}
        selectedExempt={selectedExempt}
        setSelectedExempt={setSelectedExempt}
        selectedFormId={selectedFormId}
        setSelectedFormId={setSelectedFormId}
        teams={teams}
        forms={forms}
        saving={saving}
        onSave={handleSaveUserChanges}
        deactivating={deactivating}
        onDeactivate={handleDeactivateUser}
        onReactivate={handleReactivateUser}
        deleteDialogOpen={deleteDialogOpen}
        setDeleteDialogOpen={setDeleteDialogOpen}
        userToDelete={userToDelete}
        deleting={deleting}
        onDelete={handleConfirmDeleteUser}
        onOpenDelete={openDeleteDialog}
      />

      {/* Report Days Dialog */}
      <LogReportDialog
        open={reportDialogOpen}
        onOpenChange={setReportDialogOpen}
        reportUser={reportUser}
        reportDays={reportDays}
        setReportDays={setReportDays}
        reportFormat={reportFormat}
        setReportFormat={setReportFormat}
        onGenerate={generateReport}
      />

      {/* User Permissions Dialog */}
      <UserPermissionsDialog
        open={permissionsDialogOpen}
        onOpenChange={setPermissionsDialogOpen}
        user={permissionsUser}
        onUpdate={() => fetchData(true)}
      />

      {/* Bulk RBAC Role Assign Dialog */}
      <Dialog open={bulkRoleDialogOpen} onOpenChange={setBulkRoleDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-indigo-600" />
              Assign RBAC Role
            </DialogTitle>
            <DialogDescription>
              Assign a role to {selectedUserIds.size} selected user(s). This will overwrite their current RBAC role.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Select Role</Label>
            <Select value={bulkRoleId || "no-role"} onValueChange={v => setBulkRoleId(v === "no-role" ? "" : v)}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select a role..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no-role">Clear Role (No Role)</SelectItem>
                {bulkRoleRoles.map(r => (
                  <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkRoleDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleBulkAssignRole} disabled={bulkAssigning}>
              {bulkAssigning && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Apply to {selectedUserIds.size} user(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Telnyx Setup Dialog */}
      <TelnyxDialog
        open={telnyxDialogOpen}
        onOpenChange={setTelnyxDialogOpen}
        telnyxUser={telnyxUser}
        telnyxSettings={telnyxSettings}
        setTelnyxSettings={setTelnyxSettings}
        telnyxLoading={telnyxLoading}
        telnyxSaving={telnyxSaving}
        testingConnection={testingConnection}
        connectionTestResults={connectionTestResults}
        testCallNumber={testCallNumber}
        setTestCallNumber={setTestCallNumber}
        testSmsNumber={testSmsNumber}
        setTestSmsNumber={setTestSmsNumber}
        testSmsMessage={testSmsMessage}
        setTestSmsMessage={setTestSmsMessage}
        testingCall={testingCall}
        testingSms={testingSms}
        onSave={handleSaveTelnyxSettings}
        onTestConnection={handleTestConnection}
        onTestCall={handleTestCall}
        onTestSms={handleTestSms}
      />

      {/* Email Merge Dialog */}
      <UserEmailMergeDialog
        open={emailMergeDialogOpen}
        onOpenChange={setEmailMergeDialogOpen}
        selectedUsers={getSelectedUsers()}
        emailSubject={emailSubject}
        setEmailSubject={setEmailSubject}
        emailBody={emailBody}
        setEmailBody={setEmailBody}
        fromEmail={fromEmail}
        setFromEmail={setFromEmail}
        connectedEmails={connectedEmails}
        sendingEmails={sendingEmails}
        rewritingWithAi={rewritingWithAi}
        onSend={handleSendBulkEmails}
        onRewriteWithAi={handleAiRewrite}
        insertVariable={insertVariable}
        currentUser={currentUser}
        selectedUserIds={selectedUserIds}
      />

      {/* Login History Dialog */}
      <LoginHistoryDialog
        open={loginHistoryDialogOpen}
        onOpenChange={setLoginHistoryDialogOpen}
        loginHistoryUser={loginHistoryUser}
        loginHistory={loginHistory}
        loadingHistory={loadingHistory}
      />

      {/* Activity Report Dialog */}
      <ActivityReportDialog
        open={activityReportDialogOpen}
        onOpenChange={setActivityReportDialogOpen}
        activityReportUser={activityReportUser}
        activityDateRange={activityDateRange}
        setActivityDateRange={setActivityDateRange}
        activityCustomStart={activityCustomStart}
        setActivityCustomStart={setActivityCustomStart}
        activityCustomEnd={activityCustomEnd}
        setActivityCustomEnd={setActivityCustomEnd}
        activityTypes={activityTypes}
        setActivityTypes={setActivityTypes}
        activityReportType={activityReportType}
        setActivityReportType={setActivityReportType}
        activityReportData={activityReportData}
        setActivityReportData={setActivityReportData}
        loadingActivityReport={loadingActivityReport}
        onGenerate={handleGenerateActivityReport}
      />

      {/* HR Data Dialog */}
      <HrDataDialog
        open={hrDataDialogOpen}
        onOpenChange={setHrDataDialogOpen}
        hrDataUser={hrDataUser}
        hrData={hrData}
        setHrData={setHrData}
        loadingHrData={loadingHrData}
        savingHrData={savingHrData}
        onSave={handleSaveHrData}
      />

    </div>
  );
}

